import { CSSProperties, useEffect, useState } from "react"
import { Switch, Route, useLocation, useHistory } from "react-router-dom"
import { NAV_BAR_HEIGHT_MOBILE, NAV_BAR_WIDTH_COLLAPSED, PlaybookType } from "modules/Const"
import useScreen from "Hooks/useScreen"
import { useNavbar } from "Hooks/useNavbar"
import useBrand from 'Hooks/useBrand'
import useOrganization from "Hooks/useOrganization"
import useMaestroChat from "Hooks/useMaestroChat"
import { styx } from "modules/Utils"

import SettingsPage from "pages/SettingsPage/SettingsPage"
import WebsiteHomepage from "pages/WebsitePage/Website"
import Dashboard from "pages/DashboardPage/DashboardPage"
import MarketingPage from "pages/MarketingPage/MarketingPage"
import Fans from "pages/FansPage/Fans";
import CreateNewSymphonyProfile from "pages/AddArtist/CreateNewSymphonyProfile"
import MarketingSuccessPage from "../pages/MarketingPage/SuccessPage"
import MarketingCampaignDetails from "pages/MarketingCampaignDetails/MarketingCampaignDetails"
import PreSaveProvider from "pages/MarketingPage/Data/PreSaveProvider"
import PreSave from "pages/MarketingPage/PreSave/PreSave"
import GetPlaylisted from "pages/MarketingPage/Playlisting"
import VideoViews from "pages/MarketingPage/VideoAds/VideoViews"
import CreativeGenerator from "pages/MarketingPage/CreativeGenerator"
import EnterEmail from "pages/EnterEmail/EnterEmail"
import ReferPage from "pages/Refer"
import AdminVideoViewsDataUpload from "pages/Admin/VideoViewsDataUpload"
import CampaignReports from "pages/Admin/reports"
import Learn from "pages/LearnPage/Learn"
import KPITrends from "pages/Admin/kpis"
import AffiliatesDashboard from "pages/Admin/AffiliatesDashboard"
import SourcesTrends from "pages/Admin/sources"
import TopPerforming from "pages/Admin/TopPerforming"
import UploadEmailsByCsv from "pages/FansPage/Components/UploadEmailsByCsv/UploadEmailsByCsv"
import AdminOnboarding from "pages/OrganizationPage/componentsOrganization/AdminOnboarding"
import ElementsPage from "pages/ElementsPage"
import TestApiPage from "pages/TestApiPage"
import TiktokCallbackPage from "pages/TikTokLoginCallback"
import CustomerSupportBrandEditingTools from "pages/CustomerSupportBrandEditingTools"
import MessageYourFans from "pages/FansPage/Components/MessageYourFans/MessageYourFans"
import EmailDetailsV2 from "pages/EmailDetailsV2"
import TablePage from "pages/TablePage"
import Clipper from "pages/Admin/Clipper"
import AssistantPage from "pages/AssistantPage"
import NotFoundPage from "pages/NotFoundPage"
import AdCreatorDemo from "components/AdCreatorDemo"

import MarketingFlow2 from "components/MarketingFlow2"
import CampaignSubmissionView from "components/CampaignSubmissionView"
import WebsiteBuilder from "components/websiteBuilder/WebsiteBuilder"
import CreateNewCampaignModalV2 from "components/CreateNewCampaignModalV2"
import CreateNewWebsiteModalV2 from "components/shareable/CreateNewWebsiteModalV2"
import WebsitePage from "pages/WebsitePage/components/WebsitePage"
import AllWebsites from "pages/AllWebsites"
import AddArtistV2 from "pages/AddArtistV2" // Existing onboarding
import AddBrandV3 from "pages/AddBrandV3"; // New onboarding (renamed)
import OrganizationPageV2 from "pages/OrganizationPageV2"
import ApiKeysPage from "pages/ApiKeyPage"
import CampaignsDashboard from "pages/CampaignsDashboard"
import PaymentPastDueModal from "components/shareable/PaymentPastDueModal"


// Define post-auth routes that require authentication
const postAuthRoutes = [
    {
        path: '/setup',
        exact: false,
        component: <AddBrandV3 />,
    },
    {
        path: ['/', '/assistant'],
        exact: true,
        component: <AssistantPage />,
    },
    {
        path: '/chats/:chatId',
        exact: false,
        component: <AssistantPage />,
    },
    {
        path: '/marketing2/new/:playbookType',
        exact: false,
        component: <MarketingFlow2 />,
    },
    {
        path: '/marketing2/draft/:draftId',
        exact: false,
        component: <MarketingFlow2 />,
    },
    {
        path: '/marketing2/submission/:campaignId',
        exact: false,
        component: <CampaignSubmissionView />,
    },
    {
        path: '/email',
        exact: true,
        component: <EnterEmail />,
    },
    {
        path: '/marketing',
        exact: true,
        component: <MarketingPage />,
    },
    {
        path: '/dashboard',
        exact: true,
        component: <Dashboard />,
    },
    {
        path: '/marketing/campaigns/:campaignId',
        exact: true,
        component: <MarketingCampaignDetails className="marketingCampaignDetailsComponentPostAuth" />,
    },
    {
        path: '/marketing/new/creatives',
        exact: true,
        component: <CreativeGenerator />,
    },
    {
        path: '/marketing/new/presave',
        exact: true,
        component: (
            <PreSaveProvider>
                <PreSave setGoal={() => null} />
            </PreSaveProvider>
        ),
    },
    {
        path: '/marketing/new/music-streams',
        exact: true,
        component: <MarketingFlow2 playbookType={PlaybookType.INCREASE_MUSIC_STREAMS} />,
    },
    {
        path: '/marketing/new/grow-playlist-followers',
        exact: true,
        component: <MarketingFlow2 playbookType={PlaybookType.GROW_PLAYLIST_FOLLOWERS} />,
    },
    {
        path: '/marketing/new/grow-instagram-engagement',
        exact: true,
        component: <MarketingFlow2 playbookType={PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT} />,
    },
    {
        path: '/marketing/new/playlists',
        exact: true,
        component: <GetPlaylisted />,
    },
    {
        path: '/marketing/new/video-views',
        exact: true,
        component: <VideoViews />,
    },
    {
        path: '/marketing/new/tiktok-ads',
        exact: true,
        component: <MarketingFlow2 playbookType={PlaybookType.TIKTOK_BOOST_ENGAGEMENT} />,
    },
    {
        path: '/marketing/new/link-clicks',
        exact: true,
        component: <MarketingFlow2 playbookType={PlaybookType.INCREASE_LINK_CLICKS} />,
    },
    {
        path: '/add-artist-OLD',
        exact: false,
        component: <CreateNewSymphonyProfile />,
    },
    {
        path: ['/add-artist', '/goals'],
        exact: false,
        component: <AddArtistV2 />,
    },
    {
        path: '/marketing/success',
        exact: false,
        component: <MarketingSuccessPage />,
    },
    {
        path: '/tiktok/callback',
        exact: false,
        component: <TiktokCallbackPage />,
    },
    {
        path: '/website',
        exact: true,
        component: <WebsitePage />,
    },
    {
        path: '/website-builder',
        exact: true,
        component: <WebsiteBuilder />,
    },
    {
        path: '/website-builder/:brandSlug/:websiteSlug',
        exact: true,
        component: <WebsiteBuilder />,
    },
    {
        path: '/fans/campaigns/:id',
        exact: false,
        component: <EmailDetailsV2 />,
    },
    {
        path: '/fans',
        exact: false,
        component: <Fans />,
    },
    {
        path: '/import-fans',
        exact: false,
        component: <UploadEmailsByCsv />,
    },
    {
        path: '/message-your-fans',
        exact: false,
        component: <MessageYourFans />,
    },
    {
        path: '/website/:websiteType/:websiteSlug',
        exact: false,
        component: <WebsiteHomepage />,
    },
    {
        path: '/elementspage',
        exact: true,
        component: <ElementsPage />,
    },
    {
        path: '/testapi',
        exact: true,
        component: <TestApiPage />,
    },
    {
        path: '/admintools',
        exact: true,
        component: <CustomerSupportBrandEditingTools />,
    },
    {
        path: '/allwebsites',
        exact: true,
        component: <AllWebsites />,
    },
    {
        path: '/learn',
        exact: false,
        component: <Learn />,
    },
    {
        path: '/settings',
        exact: true,
        component: <SettingsPage />,
    },
    {
        path: '/team',
        exact: true,
        component: <OrganizationPageV2 />,
    },
    {
        path: '/admin/onboarding',
        exact: true,
        component: <AdminOnboarding />,
    },
    {
        path: '/refer',
        exact: true,
        component: <ReferPage />,
    },
    {
        path: '/admin/video-views-data-upload',
        exact: true,
        component: <AdminVideoViewsDataUpload />,
    },
    {
        path: '/admin/reports',
        exact: true,
        component: <CampaignReports />,
    },
    {
        path: '/admin/top-performing',
        exact: true,
        component: <TopPerforming />,
    },
    {
        path: '/admin/kpis',
        exact: true,
        component: <KPITrends />,
    },
    {
        path: '/admin/sources',
        exact: true,
        component: <SourcesTrends />,
    },
    {
        path: '/admin/affiliates',
        exact: true,
        component: <AffiliatesDashboard />,
    },
    {
        path: '/tools/clipper',
        exact: true,
        component: <Clipper />,
    },
    {
        path: '/tools/ad-creator',
        exact: true,
        component: <AdCreatorDemo />,
    },
    {
        path: '/table',
        exact: true,
        component: <TablePage />,
    },
    {
        path: '/api-keys',
        exact: true,
        component: <ApiKeysPage />,
    },
    {
        path: '/campaigns-dashboard',
        exact: true,
        component: <CampaignsDashboard />,
    },
    {
        path: '*',
        exact: false,
        component: <NotFoundPage />,
    },
];

export const checkIsPostAuthRoute = (pathname: string): boolean => {
    return postAuthRoutes.some(route => {
        const paths = Array.isArray(route.path) ? route.path : [route.path];
        return paths.some(path => {
            if (path.includes(':')) {
                // Handle dynamic routes like /marketing/campaigns/:campaignId
                const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
                const regex = new RegExp(`^${pathPattern}$`);
                return regex.test(pathname);
            }
            return path === pathname;
        });
    });
};

const PostAuthRoutes = () => {
    const location = useLocation()
    const history = useHistory()
    const { mStyle, mobileView, cssCalc } = useScreen()
    const {
        isCollapsed,
        shouldHideNavbar,
        isMobileNavbarShown,
        isWebsiteChildPage
    } = useNavbar();
    const { currentBrand, getIsPaymentFailed, isGrandfathered, isLoadingBrand } = useBrand() // Added isGrandfathered and isLoadingBrand
    // const { currentStep: onboardingV3Step } = useOnboardingV3(); // To check onboarding status
    const { organization } = useOrganization()
    const { inChatMode } = useMaestroChat()

    const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(getIsPaymentFailed())

    let postAuthRoutesWidth = '100%'

    // if (!mobileView && isCollapsed) {
    //     // NOTE: if showing the navbar
    //     if (!shouldHideNavbar(location.pathname, organization, currentBrand)) {
    //         postAuthRoutesWidth = `calc(100% - ${NAV_BAR_WIDTH_COLLAPSED}px)`
    //     }
    // }

    let overflowX = 'hidden'

    const getPostAuthRoutesHeight = () => {
        // In chat mode on mobile, use full viewport height
        if (mobileView && inChatMode) {
            return '100%'
        }

        if (mobileView && isMobileNavbarShown()) {
            let heightOffset = NAV_BAR_HEIGHT_MOBILE

            return cssCalc(heightOffset)
        }

        return cssCalc()
    }

    if (isWebsiteChildPage()) {
        overflowX = 'visible'
    }

    useEffect(() => {
        setShowPaymentFailedModal(getIsPaymentFailed())
    }, [currentBrand?.id])

    // Redirect logic for onboarding
    // useEffect(() => {
    //     if (isLoadingBrand) return; // Wait for brand data to be loaded

    //     const { pathname } = location;
    //     // If dashboard_initialized is false, it implies onboarding is not complete for this brand.
    //     const needsOnboarding = !currentBrand?.dashboard_initialized && !isGrandfathered;

    //     if (pathname === '/add-artist' && isGrandfathered) {
    //         // Grandfathered user on /setup page, redirect to dashboard
    //         history.push('/');
    //     } else if (pathname !== '/add-artist' && needsOnboarding && currentBrand?.id) {
    //         // New user not on /setup page and needs onboarding, redirect to /setup
    //         // Avoid redirect if no brand yet or if already on a public-ish post-auth page like /settings
    //         // This might need refinement to avoid redirect loops if some pages are accessible during onboarding.
    //         const nonOnboardingPages = ['/settings', '/api-keys']; // Add other paths accessible before onboarding completion
    //         if (!nonOnboardingPages.some(p => pathname.startsWith(p))) {
    //              history.push('/add-artist');
    //         }
    //     }
    //     // AddBrandV3 handles redirecting away from /setup upon completion.

    // }, [location.pathname, currentBrand, isGrandfathered, history, isLoadingBrand]);


    return (
        <div
            id="main-body"
            className="postAuthRoutesComponent"
            style={styx(
                mStyle(sty, 'postAuthRoutesComponent'),
                { height: getPostAuthRoutesHeight() },
                { width: postAuthRoutesWidth },
                { overflowX: overflowX as CSSProperties['overflowX'] },
            )}
        >
            <Switch>
                {postAuthRoutes.map((route, index) => (
                    <Route
                        key={index}
                        exact={route.exact}
                        path={route.path}
                        children={route.component}
                    />
                ))}
            </Switch>
            {/* <PaymentFailedModal
            className="paymentFailedModalPostAuthRoutes"
            isOpen={showPaymentFailedModal}
            onClose={() => setShowPaymentFailedModal(false)}
        /> */}
            <PaymentPastDueModal autoShow={true} />

            {currentBrand?.id &&
                <>
                    <CreateNewCampaignModalV2
                        className="createNewCampaignModalPostAuthRoutesV2"
                    />

                    {/* useMaestroInstagramPosts.ts */}
                    {/* <MaestroInstagramAnalyzer
                        className="maestroInstagramAnalyzerPostAuthRoutes"
                    /> */}

                    {/* useWebsiteBuilder.ts */}
                    <CreateNewWebsiteModalV2
                        className="createNewWebsiteModalPostAuthRoutesV2"
                    />
                </>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    postAuthRoutesComponent: {
        display: "flex",
        flexDirection: "column",
        marginLeft: 'auto',
        // backgroundColor: 'white',
    },
    postAuthRoutesComponent_mobile: {
        marginLeft: 0,
        // backgroundColor: 'white',
    }
}

export default PostAuthRoutes